import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { ScrollReveal } from "@/components/ScrollReveal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { NotificationBell } from "@/components/NotificationBell";
import CFOVirtualPanel from "@/components/dashboard/CFOVirtualPanel";
import PrioritiesPanel from "@/components/dashboard/PrioritiesPanel";
import MyGraftonProfileCard from "@/components/dashboard/MyGraftonProfileCard";
import { LivePipelineWidget } from "@/components/dashboard/LivePipelineWidget";
import { LiveCashBanner } from "@/components/dashboard/LiveCashBanner";
import {
  LogOut,
  TrendingUp,
  Users,
  UserPlus,
  Euro,
  Brain,
  FileText,
  Landmark,
  Mail,
  Calculator,
  ChevronRight,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Loader2,
  RefreshCw,
  FileBarChart,
  MessageCircle,
  HelpCircle,
  User,
  Globe,
  CreditCard,
  Bell,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { toast } from "sonner";
import { GraftonChat } from "@/components/GraftonChat";
import { useDashboardData } from "@/hooks/useDashboardData";
import { WebsiteTab } from "@/components/dashboard/WebsiteTab";
import { PaymentTab } from "@/components/dashboard/PaymentTab";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Euro,
  Users,
  UserPlus,
  Landmark,
};

const statusColors: Record<string, string> = {
  done: "bg-emerald-500/15 text-emerald-400",
  pending: "bg-primary/15 text-primary",
  urgent: "bg-destructive/15 text-red-400",
};
const statusLabels: Record<string, string> = {
  done: "Fait",
  pending: "A faire",
  urgent: "Urgent",
};

const Dashboard = () => {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [notifications, setNotifications] = useState<any>(null);
  const [notifsExpanded, setNotifsExpanded] = useState(false);
  const { kpis, revenueData, treasuryData, actions, documents, loading: dataLoading } = useDashboardData(user?.id);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [authLoading, user, navigate]);

  // Fetch notifications
  useEffect(() => {
    if (!user) return;
    const fetchNotifs = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/notifications`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setNotifications(data);
        }
      } catch {}
    };
    fetchNotifs();
  }, [user]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) return null;

  const greeting = user.name || user.email;

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background flex w-full" data-testid="dashboard-container">
        <DashboardSidebar />

        <div className="flex-1 flex flex-col min-w-0">
          {/* Top bar */}
          <nav className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
            <div className="flex h-16 items-center justify-between px-4">
              <div className="flex items-center gap-3">
                <SidebarTrigger className="text-muted-foreground" />
                <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
                  <span className="text-primary-foreground font-bold text-sm">G</span>
                </div>
                <span className="font-semibold text-lg tracking-tight">Grafton IA</span>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-primary/15 text-primary px-2 py-0.5 rounded-full hidden sm:inline">
                  {user?.email === "broulou.joel@me.com" ? "Fondateur" : "Starter"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" className="gap-1.5 hidden md:flex">
                  <FileBarChart className="h-4 w-4" /> Rapport
                </Button>
                <Button variant="ghost" size="sm" className="gap-1.5 hidden md:flex">
                  <MessageCircle className="h-4 w-4" /> Discussions
                </Button>
                <Button variant="ghost" size="sm" className="gap-1.5 hidden md:flex">
                  <HelpCircle className="h-4 w-4" /> Assistance
                </Button>
                <NotificationBell />
                <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
                <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={signOut} className="gap-1">
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">Déconnexion</span>
                </Button>
              </div>
            </div>
          </nav>

          <main className="flex-1 px-4 md:px-8 py-8 space-y-8">
            <ScrollReveal>
              <div className="flex items-center justify-between">
                <div>
                  <h1 data-testid="dashboard-greeting" className="text-3xl md:text-4xl font-bold tracking-tight" style={{ lineHeight: 1.15 }}>
                    Bonjour, {greeting}
                  </h1>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-sm text-muted-foreground">Grafton IA actif</span>
                  </div>
                </div>
                <span className="text-sm text-muted-foreground hidden sm:block">
                  {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} · {new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <PrioritiesPanel />
            </ScrollReveal>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {kpis.map((kpi, i) => (
                <ScrollReveal key={kpi.label} delay={i * 80}>
                  <Card className="relative overflow-hidden hover:border-primary/30 transition-all duration-300 hover:shadow-[0_4px_24px_hsl(var(--glow-amber)/0.06)]">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">{kpi.label}</p>
                          <p className="text-2xl font-bold tabular-nums">
                            {kpi.label.includes("Taux") ? `${kpi.value}%` : kpi.value}
                          </p>
                          <div className="flex items-center gap-1 text-xs">
                            {kpi.trend === "up" ? (
                              <ArrowUpRight className="h-3.5 w-3.5 text-emerald-400" />
                            ) : (
                              <ArrowDownRight className="h-3.5 w-3.5 text-red-400" />
                            )}
                            <span className={kpi.trend === "up" ? "text-emerald-400" : "text-red-400"}>
                              {kpi.change_value}
                            </span>
                            <span className="text-muted-foreground">{kpi.sub_label}</span>
                          </div>
                        </div>
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          {(() => { const Icon = iconMap[kpi.icon] || Euro; return <Icon className="h-5 w-5 text-primary" />; })()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </ScrollReveal>
              ))}
            </div>

            {/* CFO Virtuel — cloud (Claude) or local (Ollama/Gemma2) */}
            <ScrollReveal>
              <CFOVirtualPanel />
            </ScrollReveal>

            {/* Mon profil Grafton — activity tracker */}
            <ScrollReveal>
              <MyGraftonProfileCard />
            </ScrollReveal>

            {/* Pipeline HubSpot live — connecté à l'API Grafton externe */}
            <ScrollReveal>
              <LivePipelineWidget />
            </ScrollReveal>


            {/* Charts row */}
            {notifications && (notifications.new_registrations_count > 0 || notifications.new_contacts_count > 0) && (
              <ScrollReveal>
                <Card className="border-amber-500/20 shadow-[0_4px_24px_hsl(38_90%_55%/0.06)]" data-testid="notifications-card">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Bell className="h-5 w-5 text-amber-400" />
                        Notifications
                        <span className="text-xs bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full font-normal">
                          {(notifications.new_registrations_count || 0) + (notifications.new_contacts_count || 0)} cette semaine
                        </span>
                      </CardTitle>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs gap-1 h-7"
                        onClick={() => setNotifsExpanded(!notifsExpanded)}
                        data-testid="toggle-notifications"
                      >
                        {notifsExpanded ? "Réduire" : "Voir tout"}
                        <ChevronRight className={`h-3 w-3 transition-transform ${notifsExpanded ? "rotate-90" : ""}`} />
                      </Button>
                    </div>
                  </CardHeader>
                  {notifsExpanded && (
                    <CardContent>
                      <div className="space-y-2">
                        {notifications.recent_registrations?.map((reg: any, i: number) => (
                          <div key={`reg-${i}`} className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                            <div className="h-8 w-8 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
                              <UserPlus className="h-4 w-4 text-emerald-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">Nouvelle inscription : <span className="text-emerald-400">{reg.name || reg.email}</span></p>
                              <p className="text-[10px] text-muted-foreground">{reg.email} — {reg.created_at ? new Date(reg.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</p>
                            </div>
                            <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate("/ventes")}>
                              Voir <ChevronRight className="h-3 w-3 ml-1" />
                            </Button>
                          </div>
                        ))}
                        {notifications.recent_contacts?.map((contact: any, i: number) => (
                          <div key={`contact-${i}`} className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/10">
                            <div className="h-8 w-8 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                              <MessageCircle className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">Message de : <span className="text-primary">{contact.prenom} {contact.nom}</span></p>
                              <p className="text-[10px] text-muted-foreground">{contact.email} — {contact.motif || "Contact"}</p>
                            </div>
                            <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate("/emails")}>
                              Voir <ChevronRight className="h-3 w-3 ml-1" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              </ScrollReveal>
            )}

            {/* Charts row */}
            <div className="grid lg:grid-cols-2 gap-6">
              <ScrollReveal delay={0}>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-primary" />
                      Chiffre d'affaires
                    </CardTitle>
                    <CardDescription>Evolution mensuelle 2026</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={revenueData}>
                          <defs>
                            <linearGradient id="caGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="hsl(38 90% 55%)" stopOpacity={0.3} />
                              <stop offset="100%" stopColor="hsl(38 90% 55%)" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 20% 20%)" />
                          <XAxis dataKey="month" stroke="hsl(220 15% 55%)" fontSize={12} />
                          <YAxis stroke="hsl(220 15% 55%)" fontSize={12} tickFormatter={(v) => `${v / 1000}k`} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "hsl(222 28% 13%)",
                              border: "1px solid hsl(222 20% 20%)",
                              borderRadius: "8px",
                              color: "hsl(220 20% 92%)",
                            }}
                            formatter={(v: number) => [`${v.toLocaleString("fr-FR")} EUR`, "CA"]}
                          />
                          <Area type="monotone" dataKey="ca" stroke="hsl(38 90% 55%)" fill="url(#caGradient)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>

              <ScrollReveal delay={100}>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Landmark className="h-5 w-5 text-primary" />
                      Plan de trésorerie
                    </CardTitle>
                    <CardDescription>Entrées, sorties et solde net</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <LiveCashBanner />
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={treasuryData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 20% 20%)" />
                          <XAxis dataKey="month" stroke="hsl(220 15% 55%)" fontSize={12} />
                          <YAxis stroke="hsl(220 15% 55%)" fontSize={12} tickFormatter={(v) => `${v / 1000}k`} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "hsl(222 28% 13%)",
                              border: "1px solid hsl(222 20% 20%)",
                              borderRadius: "8px",
                              color: "hsl(220 20% 92%)",
                            }}
                            formatter={(v: number) => [`${v.toLocaleString("fr-FR")} EUR`]}
                          />
                          <Bar dataKey="entrees" name="Entrées" fill="hsl(150 60% 45%)" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="sorties" name="Sorties" fill="hsl(0 60% 50%)" radius={[4, 4, 0, 0]} />
                          <Line type="monotone" dataKey="solde" name="Solde" stroke="hsl(38 90% 55%)" strokeWidth={2} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>
            </div>

            {/* Grafton IA Suggestions */}
            <ScrollReveal>
              <Card className="border-primary/20 shadow-[0_4px_24px_hsl(var(--glow-amber)/0.06)]">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-primary" />
                      Propositions de Grafton IA
                    </CardTitle>
                    <Button variant="ghost" size="sm" disabled={aiLoading}>
                      <RefreshCw className={`h-4 w-4 mr-1 ${aiLoading ? "animate-spin" : ""}`} />
                      Actualiser
                    </Button>
                  </div>
                  <CardDescription>Recommandations basées sur l'analyse de vos données</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {aiSuggestions.map((s, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-3 p-4 rounded-lg bg-secondary/50 border border-border/50 hover:border-primary/20 transition-colors"
                      >
                        <Brain className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                        <p className="text-sm leading-relaxed">{s}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </ScrollReveal>

            {/* Bottom grid */}
            <div className="grid lg:grid-cols-2 gap-6">
              <ScrollReveal delay={0}>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Activity className="h-5 w-5 text-primary" />
                      Actions principales
                    </CardTitle>
                    <CardDescription>Tâches en cours et à venir</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {actions.map((action, i) => (
                        <div
                          key={action.id || i}
                          className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/50 hover:border-primary/20 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[action.status]}`}>
                              {statusLabels[action.status]}
                            </span>
                            <span className="text-sm truncate">{action.label}</span>
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0 ml-2">{action.due_date}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>

              <ScrollReveal delay={100}>
                <Card>
                  <CardHeader>
                    <Tabs defaultValue="email" className="w-full">
                      <TabsList className="w-full">
                        <TabsTrigger value="email" className="flex-1 gap-1.5">
                          <Mail className="h-4 w-4" /> Emails
                        </TabsTrigger>
                        <TabsTrigger value="compta" className="flex-1 gap-1.5">
                          <Calculator className="h-4 w-4" /> Comptabilité
                        </TabsTrigger>
                        <TabsTrigger value="banque" className="flex-1 gap-1.5">
                          <Landmark className="h-4 w-4" /> Banque
                        </TabsTrigger>
                        <TabsTrigger value="website" className="flex-1 gap-1.5">
                          <Globe className="h-4 w-4" /> Site
                        </TabsTrigger>
                        <TabsTrigger value="payment" className="flex-1 gap-1.5">
                          <CreditCard className="h-4 w-4" /> Paiement
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="email" className="mt-4">
                        <div className="p-4 rounded-lg bg-secondary/30 border border-border/50 text-center">
                          <Mail className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">Connectez votre email pour voir vos messages ici.</p>
                          <Button variant="outline" size="sm" className="mt-3">Connecter mon email</Button>
                        </div>
                      </TabsContent>

                      <TabsContent value="compta" className="mt-4">
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground p-3">Le détail comptable réel (Qonto) est dans l'onglet Comptabilité.</p>
                          {([] as { label: string; value: string; count: number | null }[]).map((item, i) => (
                            <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                              <span className="text-sm">{item.label}</span>
                              <div className="flex items-center gap-2">
                                {item.count && (
                                  <span className="text-xs bg-primary/15 text-primary px-1.5 py-0.5 rounded-full">
                                    {item.count}
                                  </span>
                                )}
                                <span className="text-sm font-semibold tabular-nums">{item.value}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </TabsContent>

                      <TabsContent value="banque" className="mt-4">
                        <div className="p-4 rounded-lg bg-secondary/30 border border-border/50 text-center">
                          <Landmark className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">Connectez votre banque pour synchroniser vos transactions.</p>
                          <Button variant="outline" size="sm" className="mt-3">Connecter ma banque</Button>
                        </div>
                      </TabsContent>
                      <TabsContent value="website" className="mt-4">
                        <WebsiteTab />
                      </TabsContent>
                      <TabsContent value="payment" className="mt-4">
                        <PaymentTab />
                      </TabsContent>
                    </Tabs>
                  </CardHeader>
                </Card>
              </ScrollReveal>
            </div>

            {/* Documents */}
            <ScrollReveal>
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      Documents récents
                    </CardTitle>
                    <Button variant="ghost" size="sm">
                      Voir tout <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {documents.map((doc, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30 border border-border/50 hover:border-primary/20 transition-colors cursor-pointer group"
                      >
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{doc.name}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{doc.type}</span>
                            <span>-</span>
                            <span>{doc.doc_date}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </ScrollReveal>
          </main>

          <GraftonChat
            variant="dashboard"
            subtitle="Co-pilote opérationnel — stateful"
            welcomeMessage="Pose-moi n'importe quelle question sur ton activité. Je vois tes chiffres réels et je m'en souviens."
            quickPrompts={[
              "Analyse mon CA et propose 3 actions cette semaine",
              "Qui sont mes prospects les plus chauds ?",
              "Comment augmenter ma marge de 10% ?",
              "Rédige un mail de relance pour mes qualifiés",
            ]}
            context={{
              revenue: kpis.find(k => k.icon === "Euro")?.value ?? 0,
              clients: kpis.find(k => k.icon === "Users")?.value ?? 0,
              prospects: kpis.find(k => k.icon === "UserPlus")?.value ?? 0,
              treasury: kpis.find(k => k.icon === "Landmark")?.value ?? 0,
            }}
          />
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Dashboard;
